import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { _get } from "../services/apiClient";
import Invoice from "./Invoice";
import { HiOutlineDownload } from "react-icons/hi";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { ShopInfo } from "../shopinfo";
import { formatCurrency } from "../helper/currency";

const OrderDetail = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  const [order, setOrder] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await _get(`/orders/orderview?token=${token}`);
        setOrder(res.data.order);
        setStatusHistory(res.data.statusHistory || []);
      } catch (err) {
        console.error("Error fetching order:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [token]);

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <p>Loading order...</p>
      </div>
    );

  if (!order)
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <p className="h5 text-muted">Order not found</p>
      </div>
    );

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB");
  };

  const steps = [
    "placed",
    "accept",
    "ready for delivery",
    "delivery pickup",
    "out for delivery",
    "completed",
  ];

  const statusMap = {};
  statusHistory.forEach((item) => {
    statusMap[item.status.toLowerCase()] = item.createdAt;
  });

  // Ensure "placed" always has a timestamp
  if (!statusMap["placed"]) {
    statusMap["placed"] = order.createdAt;
  }

  const currentStepIndex = steps.findIndex(
    (step) => step.toLowerCase() === order.status.toLowerCase()
  );

  return (
    <section className="py-4">
      <div className="container">
        {/* ===== Header with Logo & Company ===== */}
        <div className="text-center mb-4">
          <img
            src={"/img/logo_web.png"}
            alt="Company Logo"
            style={{ width: "80px", height: "80px", objectFit: "contain" }}
          />
          <h2 className="mt-2 mb-0 text-dark font-weight-bold">
            {ShopInfo.Name}
          </h2>
          <p className="text-muted small mb-0">{ShopInfo.Address}</p>
          <p className="text-muted small">
            {ShopInfo.Phone} | WhatsApp: {ShopInfo.Whatsapp}
          </p>

        </div>

        {/* ===== Order Card ===== */}
        <div className="p-3 rounded shadow-sm bg-white">
          {/* Order Header */}
          <div className="d-flex flex-wrap border-bottom pb-3 align-items-center">
            <div className="text-muted mr-3 mb-2 mb-sm-0">
              <img
                alt="#"
                src={`${process.env.REACT_APP_IMAGE_BASE_URL}${order.products[0]?.images[0]}`}
                className="img-fluid order_img rounded"
                style={{
                  maxWidth: "80px",
                  maxHeight: "80px",
                  objectFit: "cover",
                }}
              />
            </div>
            <div>
              <p className="mb-0 font-weight-bold">ORDER #{order.orderId}</p>
            </div>
            <div className="ml-auto mt-2 mt-sm-0 text-right">
              <span
                style={{
                  backgroundColor: "#e6f4ea",
                  color: "#1e7e34",
                  fontSize: "13px",
                  padding: "6px 14px",
                  borderRadius: "20px",
                  fontWeight: "500",
                  letterSpacing: "0.3px",
                  textTransform: "capitalize",
                }}
              >
                {order.status}
              </span>
              <p
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  marginTop: "6px",
                  marginBottom: "0",
                  color: "#6c757d",
                }}
              >
                {/* <i className="feather-clock" style={{ marginRight: "4px" }}></i>
                {formatDate(order.createdAt)} */}
                <PDFDownloadLink
                  document={
                    <Invoice
                      data={{
                        ...order,
                        totalPrice: order.amount,
                        items: order.products.map(p => ({
                          name: p.productTitle,
                          price: p.price,
                          quantity: p.quantity,
                        }))
                      }}
                    />
                  }
                  fileName={`Invoice-${order.orderId}.pdf`}
                  style={{
                    textDecoration: "none",
                    padding: "6px 12px",
                    backgroundColor: "#007bff",
                    color: "white",
                    borderRadius: "4px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer"
                  }}
                >
                  {({ loading }) => loading ? "Preparing Invoice..." : <>
                    <HiOutlineDownload /> Invoice
                  </>}
                </PDFDownloadLink>

              </p>
            </div>

          </div>

          {/* Products */}
          <div className="pt-3">
            <ul className="item p-1">
              {order.products.map((item, idx) => (
                <li key={idx} style={{ listStyleType: "none" }}>
                  <div className="small d-flex justify-content-between">
                    <span className="font-weight-bold">
                      {item.productTitle} x {item.quantity}
                    </span>
                    <span className="text-muted">
                      {formatCurrency(item.price * item.quantity)}
                    </span>

                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Total */}
          <div className="text-muted small d-flex justify-content-end mt-3">
            <div className="text-right">
              <span className="d-block">Total Payment:</span>
              <span className="text-dark font-weight-bold h5 mb-0">
                {formatCurrency(order.amount)}

              </span>
            </div>
          </div>

          {/* Tracking Timeline */}
          <div className="mt-4">
            <h6 className="font-weight-bold text-center">Order Tracking</h6>
            <div
              className="d-flex flex-wrap justify-content-between mt-4 position-relative"
              style={{ gap: "1rem" }}
            >
              {steps.map((step, index) => {
                const isCompleted = index <= currentStepIndex;
                const timestamp = statusMap[step.toLowerCase()];
                return (
                  <div
                    key={index}
                    className="text-center flex-fill position-relative"
                    style={{ minWidth: "60px", flex: "1" }}
                  >
                    {/* Step Circle */}
                    <div
                      style={{
                        width: "30px",
                        height: "30px",
                        margin: "0 auto",
                        borderRadius: "50%",
                        backgroundColor: isCompleted ? "#28a745" : "#d6d6d6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontWeight: "bold",
                        position: "relative",
                        zIndex: 2,
                      }}
                    >
                      {isCompleted ? "✔" : index + 1}
                    </div>

                    {/* Step Label */}
                    <p
                      className="small mt-2 mb-0"
                      style={{
                        color: isCompleted ? "#28a745" : "#6c757d",
                        textTransform: "capitalize",
                        fontSize: "12px",
                      }}
                    >
                      {step}
                    </p>

                    {/* Timestamp */}
                    <p
                      className="small text-muted mt-1 mb-0"
                      style={{ fontSize: "10px" }}
                    >
                      {formatDate(timestamp)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ===== Footer with Company Info ===== */}
        <footer className="text-center mt-5">
          <hr />
          <p className="mb-1 font-weight-bold">{ShopInfo.Name}</p>
          <p className="small text-muted mb-0">{ShopInfo.Address}</p>
          <p className="small text-muted mb-0">GST: {ShopInfo.GSTNumber}</p>
          <p className="small text-muted mb-0">Phone: {ShopInfo.Phone}</p>
          <p className="small text-muted">
            Open: {ShopInfo.OpenTime} - {ShopInfo.CloseTime}
          </p>
        </footer>
      </div>
    </section>
  );
};

export default OrderDetail;
